import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { MeetingLink, ZoomMeetingLink } from '@adornis/bh-video-conferencing/entities/MeetingLink.js';
import { Appointment } from '@adornis/buchungsheld-db/entities/appointment.js';
import { getLecturerProductTeaching, Teaching } from '@adornis/buchungsheld-db/entities/teaching.js';
import type { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-input.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/datetime/x-date-picker-dropdown.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import type { EntityPickerOptions } from '@adornis/entity-picker/client/entity-picker-options.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/forms/x-checkbox.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, from, of } from 'rxjs';

@customElement('bh-prompt-appointment-create')
export class BHPromptAppointmentCreate extends TrDialog<Maybe<Appointment>> {
  @property({ attribute: false }) workshop = new RXController<Maybe<Workshop>>(this, undefined);
  @property({ attribute: false }) appointment = new RXController<Maybe<Appointment>>(this, undefined);
  @property({ attribute: false }) dateFrom = new RXController<Maybe<DateTime>>(this, undefined);
  @property({ attribute: false }) dateTill = new RXController<Maybe<DateTime>>(this, undefined);

  @state() private readonly _form = new FormController(this, this.appointment.observable.pipe(filter(Boolean)), {
    success: doc => {
      if (doc.start && doc.end && doc.start > doc.end) {
        XSnackbar.show('Das Startdatum muss vor dem Enddatum liegen');
        throw new Error('the start date must be before the end date');
      }
    },
    error: err => {
      console.log(err);
    },
  });

  private static add(workshop: Workshop, dateFrom?: DateTime, dateTill?: DateTime) {
    const date = (dateFrom ?? DateTime.now()) > DateTime.now() ? dateFrom! : DateTime.now();
    const startDate = date.set({ hour: 9, minute: 0 });
    const endDate = dateTill ? dateTill.set({ hour: 18, minute: 0 }) : startDate.set({ hour: 18, minute: 0 });
    return BHPromptAppointmentCreate.showPopup<Maybe<Appointment>>({
      props: {
        dateFrom,
        dateTill,
        workshop,
        appointment: new Appointment({
          start: startDate,
          end: endDate,
        }),
      },
    });
  }

  private static edit(workshop: Workshop, appointment: Appointment) {
    return this.showPopup<Maybe<Appointment>>({ props: { appointment, workshop } });
  }

  static async addOnWorkshop(workshop: Workshop, dateFrom?: DateTime, dateTill?: DateTime) {
    const appointment = await this.add(workshop, dateFrom, dateTill);
    if (!appointment) return;
    workshop.appointments.push(appointment);
  }

  static async editOnWorkshop(ap: Appointment, workshop: Workshop) {
    // clone user so that temporary changes dont affect the ui
    const tmpAppointment = new Appointment(ap.toJSON());
    const appointment = await this.edit(workshop, tmpAppointment);
    if (!appointment) return; // canceled
    const index = workshop.appointments.findIndex(data => data._id === ap._id);
    workshop.appointments.splice(index, 1, appointment); // replace entity
  }

  override async cancel(returnVal?: any): Promise<void> {
    await this.close(null);
  }

  static override get element_name() {
    return 'bh-prompt-appointment-create';
  }

  override render() {
    if (!this._form.document) return html` <x-loading-state></x-loading-state> `;

    return html`
      <x-flex space="md">
        ${this._form.document.isNew
          ? html`<x-h2>${this._translation.translate('PROMPT', 'add_appointment')}</x-h2>`
          : html`<x-h2>${this._translation.translate('PROMPT', 'edit_appointment')}</x-h2>`}

        <tr-input placeholder="Name (Optional)" ${this._form.field('name')}></tr-input>

        <!-- date is not yet determined -->
        <tr-checkbox
          outlined
          boolean-only
          enableicon="adjust"
          disableicon
          .value=${this._form.document.start === null}
          @value-picked=${(e: ValueEvent<boolean>) => {
            const dateIsTBD = e.detail.value;
            if (dateIsTBD) {
              this._form.document!.start = null;
              this._form.document!.end = null;
            } else {
              const startDate = DateTime.now().plus({ days: 1 }).set({ hour: 9, minute: 0 });
              this._form.document!.start = startDate;
              this._form.document!.end = startDate.set({ hour: 18, minute: 0 });
            }
            this.requestUpdate();
          }}
          .label=${this._translation.translate('APPOINTMENT', 'dateIsYetToBeDetermined')}
        ></tr-checkbox>

        <!-- date inputs -->
        ${(() => {
          if (!this._form.document.start) return nothing;
          return html`
            <x-date-picker-dropdown
              show-time
              placeholder="Von"
              ${this._form.field('start')}
              .isDateBlocked=${(date: DateTime) => this.dateIsBlocked(date)}
              @value-picked=${() => {
                const start = this._form.document?.start;
                const end = this._form.document?.end;

                if (start) {
                  if (!end || start > end) {
                    // Keep the same day but set to 18:00
                    this._form.document!.end = start.set({ hour: 18, minute: 0 });
                    this.requestUpdate();
                  }
                }
                this._form.validate();
              }}
            ></x-date-picker-dropdown>

            <x-date-picker-dropdown
              show-time
              placeholder="Bis"
              ${this._form.field('end')}
              .isDateBlocked=${(date: DateTime) => {
                if (!this._form.document?.start) return false;
                return this.dateIsBlocked(date);
              }}
              @value-picked=${() => this._form.validate()}
            ></x-date-picker-dropdown>
          `;
        })()}

        <x-input ${this._form.field('location')} placeholder="Ort"></x-input>

        <x-flex space="sm">
          <x-checkbox label="Keine Anwesenheitspflicht" ${this._form.field('isNonBlocking')}></x-checkbox>
          <tr-text tone="subtle">
            Wenn ein Termin keine Anwesenheit erfordert wird dieser bei der Prüfung von Überlappungen mit anderen
            Terminen ignoriert.</tr-text
          >
        </x-flex>

        ${this.renderMeetinLinkFields()}

        <x-button
          @click=${async () => {
            const docOrErr = await this._form.validate();
            if (docOrErr instanceof Appointment) this.close(docOrErr);
          }}
          >${this._translation.translate('save')}</x-button
        >
      </x-flex>
    `;
  }

  private renderMeetinLinkFields() {
    return html`
      <tr-h3 style=${{ marginTop: '1rem', fontSize: '22px', fontWeight: '500' }}>Meeting Link</tr-h3>
      <tr-dropdown-selection
        placeholder=${'Link Typ'}
        .value=${this._form.document?.meetingLink?.constructor}
        .selectables=${[MeetingLink, ZoomMeetingLink]}
        .renderString=${kind => this._translation.translate('APPOINTMENT', kind._class)}
        @value-picked=${e => {
          const _MeetingLinkKind = e.detail.value;
          this._form.document!.meetingLink = new _MeetingLinkKind({ link: '' });
          this.requestUpdate();
        }}
      ></tr-dropdown-selection>

      ${(() => {
        const meetingLink = this._form.document?.meetingLink;
        if (!meetingLink) return nothing;

        if (meetingLink instanceof ZoomMeetingLink) {
          return html`
            ${this.renderLecturerPicker(meetingLink.ownerID + '', ownerID => {
              meetingLink.ownerID = ownerID;
              this.requestUpdate();
            })}
            ${meetingLink.ownerID
              ? html` <x-input ${this._form.field('meetingLink', 'link')} placeholder="Meeting Link"></x-input> `
              : nothing}
          `;
        }

        if (meetingLink instanceof MeetingLink) {
          return html` <x-input ${this._form.field('meetingLink', 'link')} placeholder="Meeting Link"></x-input> `;
        }

        console.error('Unknown meeting link type', meetingLink);
        throw new Error('Unknown meeting link type');
      })()}
    `;
  }

  renderLecturerPicker(lecturerID: string | null, onChange: (value: string | null) => void) {
    console.log('renderLecturerPicker', lecturerID);
    return stateful(() => {
      const teachings = useObservable(
        () =>
          !!lecturerID && !!this.workshop.value?._id
            ? from(
                getLecturerProductTeaching(
                  lecturerID,
                  this.workshop.value?._id,
                )({ lecturer: { _id: 1 }, lecturerID: 1, productID: 1 }),
              )
            : of([]),
        [this.workshop.value?._id, lecturerID],
      );

      const teaching = teachings?.at(0);

      return html` <x-entity-picker
        placeholder=${'Link Verwalter'}
        .options=${{
          class: Teaching,
          filter: new AdornisFilter({
            aspects: [
              new StringAspect({
                fieldName: 'productID',
                mode: 'equals',
                value: this.workshop.value?._id,
              }),
            ],
          }),
          selectionSet: { lecturer: { username: 1 }, lecturerID: 1, productID: 1 },
        } satisfies EntityPickerOptions<typeof Teaching>}
        .renderString=${(teaching: Maybe<Teaching>) => teaching?.lecturer?.username}
        .value=${teaching?._id}
        @value-picked=${async (e: ValueEvent<Maybe<Teaching>>) => {
          const teachingID = e.detail.value as string | null;
          if (!teachingID) return onChange(null);

          const teaching = await getByID(Teaching, teachingID)({ lecturer: { _id: 1 }, lecturerID: 1, productID: 1 });
          if (!teaching) throw new Error('teaching not found ' + teachingID);
          const userID = teaching.lecturer?._id;
          if (!userID) {
            console.error('No user ID found', e, teaching);
            throw new Error('No user ID found');
          }

          onChange(userID);
        }}
      ></x-entity-picker>`;
    })();
  }

  override getInnerWrapper(options: Required<DialogOptions>) {
    const wrapperElem = super.getInnerWrapper(options);
    wrapperElem.style.setProperty('background', this.colors.white);
    wrapperElem.style.setProperty('border-radius', this.sizes.borderRadius);
    wrapperElem.style.setProperty('width', `min(550px, 95%)`);
    wrapperElem.style.setProperty('padding', this.spacing.lg);
    return wrapperElem;
  }

  dateIsBlocked(date: DateTime) {
    if (this.dateFrom.value && date < this.dateFrom.value) return true;
    if (this.dateTill.value && date > this.dateTill.value) return true;
    return false;
  }
}
